/**
 * Centralized WhatsApp message configuration.
 *
 * Single source of truth for every WhatsApp CTA on the website.
 * - WHATSAPP_MESSAGES maps a route prefix → the pre-filled message.
 *   Longest matching prefix wins, so add deeper paths above shallower ones
 *   if you want to override a section default.
 * - WHATSAPP_KEYS holds named messages for explicit overrides
 *   (e.g. registration success states or workshop-specific CTAs).
 *
 * All messages start with "Hello Chirag Sir," and end with a polite ask.
 * Keep them short, professional, and clear. No hype.
 */

import { useLocation } from "react-router-dom";
import { siteConfig } from "@/data/siteConfig";

const PHONE = siteConfig.brand.whatsapp; // "919924619959"

export const DEFAULT_WA_MESSAGE =
  "Hello Chirag Sir, I visited the Shiksha Guide website and would like to connect. Please guide me further.";

/** Route-prefix → pre-filled WhatsApp message. Longest match wins. */
export const WHATSAPP_MESSAGES: Record<string, string> = {
  // ── SG Smart Switch ──────────────────────────────────────────────
  "/register/sg-smart-switch-june-2026":
    `Hello Chirag Sir, I would like to know about the SG Smart Switch ${siteConfig.smartSwitch.next.name} batch. Please share dates and registration details when confirmed.`,
  "/apply/sg-smart-switch-june-2026":
    `Hello Chirag Sir, I would like to know about the SG Smart Switch ${siteConfig.smartSwitch.next.name} batch. Please share dates and registration details when confirmed.`,
  "/smart-switch":
    "Hello Chirag Sir, I would like to apply for the SG Smart Switch program. Please share the next steps.",
  "/services/sg-smart-switch":
    "Hello Chirag Sir, I would like to know more about SG Smart Switch. Please share details.",
  "/services/sg-sakha":
    "Hello Chirag Sir, I would like to know more about SG Sakha — the AI business automation system. Please share details.",
  "/services":
    "Hello Chirag Sir, I would like to explore Shiksha Guide services. Please guide me.",

  // ── AI Trainings ─────────────────────────────────────────────────
  "/ai-trainings/ai-workshops":
    "Hello Chirag Sir, I am interested in upcoming AI Workshops. Please share details.",
  "/ai-trainings/whatsapp-business-systems":
    "Hello Chirag Sir, I am interested in WhatsApp Business Systems training. Please share details.",
  "/ai-trainings/ai-for-msmes":
    "Hello Chirag Sir, I am interested in AI for MSMEs. Please share details.",
  "/ai-trainings/ai-adoption-consulting":
    "Hello Chirag Sir, I am interested in AI Adoption Consulting. Please guide me.",
  "/ai-trainings/digital-presence-guidance":
    "Hello Chirag Sir, I am interested in Digital Presence Guidance. Please share details.",
  "/ai-trainings/business-workflow-systems":
    "Hello Chirag Sir, I am interested in Business Workflow Systems. Please share details.",
  "/ai-trainings/productivity-execution-support":
    "Hello Chirag Sir, I am interested in Productivity & Execution Support. Please guide me.",
  "/ai-trainings/content-presentation-training":
    "Hello Chirag Sir, I am interested in Content & Presentation Training. Please share details.",
  "/ai-trainings/business-owners":
    "Hello Chirag Sir, I am a business owner and would like to explore AI training. Please guide me.",
  "/ai-trainings/working-professionals":
    "Hello Chirag Sir, I am a working professional interested in AI training. Please share details.",
  "/ai-trainings/students":
    "Hello Chirag Sir, I am interested in AI training for students. Please share details.",
  "/ai-trainings":
    "Hello Chirag Sir, I am interested in AI Trainings at Shiksha Guide. Please share details.",

  // ── Career Clarity ───────────────────────────────────────────────
  "/career-clarity/career-counseling":
    "Hello Chirag Sir, I am interested in Career Counselling and would like to understand the process. Please guide me.",
  "/career-clarity/dmit-based-understanding":
    "Hello Chirag Sir, I would like to know more about the DMIT-based understanding process. Please share details.",
  "/career-clarity/stream-selection":
    "Hello Chirag Sir, I would like guidance on stream selection. Please guide me.",
  "/career-clarity/what-after-10th":
    "Hello Chirag Sir, I would like guidance on what to do after 10th. Please share the next steps.",
  "/career-clarity/what-after-12th":
    "Hello Chirag Sir, I would like guidance on what to do after 12th. Please share the next steps.",
  "/career-clarity/india-admissions-support":
    "Hello Chirag Sir, I would like guidance on India admissions. Please share details.",
  "/career-clarity/study-abroad-guidance":
    "Hello Chirag Sir, I would like guidance regarding Study Abroad opportunities. Please guide me.",
  "/career-clarity/canada-study-pathway":
    "Hello Chirag Sir, I would like guidance on the Canada Study Pathway. Please share details.",
  "/career-clarity/visitor-dependent-visa-support":
    "Hello Chirag Sir, I would like guidance on Visitor / Dependent Visa support. Please share details.",
  "/career-clarity/skill-development-direction":
    "Hello Chirag Sir, I would like guidance on Skill Development Direction. Please share details.",
  "/career-clarity/professional-growth-clarity":
    "Hello Chirag Sir, I would like guidance on Professional Growth Clarity. Please share details.",
  "/career-clarity/sg-elevate":
    "Hello Chirag Sir, I am interested in SG Elevate. Please share details.",
  "/career-clarity/sg-focus-planner":
    "Hello Chirag Sir, I am interested in the SG Focus Planner. Please share details.",
  "/career-clarity/education-pathways":
    "Hello Chirag Sir, I would like to understand Education Pathways. Please guide me.",
  "/career-clarity/career-pathway-selector":
    "Hello Chirag Sir, I would like guidance on choosing the right career pathway. Please guide me.",
  "/career-clarity":
    "Hello Chirag Sir, I am interested in Career Clarity services. Please guide me.",

  // ── Study Abroad ─────────────────────────────────────────────────
  "/study-abroad/canada":
    "Hello Chirag Sir, I would like guidance about studying in Canada. Please share details.",
  "/study-abroad/uk":
    "Hello Chirag Sir, I would like guidance about studying in the UK. Please share details.",
  "/study-abroad/usa":
    "Hello Chirag Sir, I would like guidance about studying in the USA. Please share details.",
  "/study-abroad/australia":
    "Hello Chirag Sir, I would like guidance about studying in Australia. Please share details.",
  "/study-abroad/germany":
    "Hello Chirag Sir, I would like guidance about studying in Germany. Please share details.",
  "/study-abroad/ireland":
    "Hello Chirag Sir, I would like guidance about studying in Ireland. Please share details.",
  "/study-abroad":
    "Hello Chirag Sir, I would like guidance regarding Study Abroad opportunities. Please guide me.",

  // ── India Admissions ─────────────────────────────────────────────
  "/india-admissions":
    "Hello Chirag Sir, I would like guidance on India admissions. Please share details.",

  // ── Workshops ────────────────────────────────────────────────────
  "/workshops":
    "Hello Chirag Sir, I would like to register for an upcoming Shiksha Guide workshop. Please share the next steps.",
  "/past-workshops":
    "Hello Chirag Sir, I missed your past workshops. Please share details of upcoming ones.",

  // ── Content / Brand pages ────────────────────────────────────────
  "/blog":
    "Hello Chirag Sir, I read your blog and would like to connect for more guidance. Please guide me.",
  "/about":
    "Hello Chirag Sir, I read about Shiksha Guide and would like to connect. Please guide me further.",
  "/chirag":
    "Hello Chirag Sir, I read your profile and would like to connect. Please guide me further.",
  "/reviews-trust":
    "Hello Chirag Sir, I read the reviews and would like to connect with Shiksha Guide. Please guide me.",
  "/enquiry":
    "Hello Chirag Sir, I would like to submit an enquiry. Please guide me.",
  "/contact":
    "Hello Chirag Sir, I visited the Shiksha Guide website and would like to connect. Please guide me further.",
  "/": DEFAULT_WA_MESSAGE,
};

/** Named messages for explicit override (success states, workshops, etc.). */
export const WHATSAPP_KEYS = {
  default: DEFAULT_WA_MESSAGE,
  smartSwitchInfo: WHATSAPP_MESSAGES["/services/sg-smart-switch"],
  smartSwitchApply: WHATSAPP_MESSAGES["/smart-switch"],
  smartSwitchJune2026: WHATSAPP_MESSAGES["/register/sg-smart-switch-june-2026"],
  smartSwitchJune2026Success:
    `Hello Chirag Sir, I would like details about the SG Smart Switch ${siteConfig.smartSwitch.next.name} batch. Please share the next steps.`,
  smartSwitchSeptember2026:
    `Hello Chirag Sir, I am interested in the SG Smart Switch ${siteConfig.smartSwitch.next.name} batch. Please share registration details when confirmed.`,
  workshopRegistration: WHATSAPP_MESSAGES["/workshops"],
  workshopSuccess:
    "Hello Chirag Sir, I have just registered for a workshop. Please confirm the details.",
  contact: WHATSAPP_MESSAGES["/contact"],
  aiWorkshops: WHATSAPP_MESSAGES["/ai-trainings/ai-workshops"],
  careerCounseling: WHATSAPP_MESSAGES["/career-clarity/career-counseling"],
  dmit: WHATSAPP_MESSAGES["/career-clarity/dmit-based-understanding"],
  studyAbroad: WHATSAPP_MESSAGES["/study-abroad"],
} as const;

export type WhatsAppKey = keyof typeof WHATSAPP_KEYS;

/** Build a fully-qualified wa.me link from a raw message string. */
export function buildWhatsAppLink(message: string): string {
  return `https://wa.me/${PHONE}?text=${encodeURIComponent(message)}`;
}

/** Resolve the best message for a given pathname (longest prefix wins). */
export function getWhatsAppMessage(pathname: string): string {
  let bestPrefix = "";
  let best = DEFAULT_WA_MESSAGE;
  for (const [prefix, msg] of Object.entries(WHATSAPP_MESSAGES)) {
    if (prefix === "/" ) continue; // home handled as fallback
    if (pathname === prefix || pathname.startsWith(prefix + "/")) {
      if (prefix.length > bestPrefix.length) {
        bestPrefix = prefix;
        best = msg;
      }
    }
  }
  if (bestPrefix === "" && pathname === "/") return WHATSAPP_MESSAGES["/"];
  return best;
}

/** Convenience: full wa.me link for a pathname. */
export function getWhatsAppLink(pathname: string): string {
  return buildWhatsAppLink(getWhatsAppMessage(pathname));
}

/**
 * React hook: returns a route-aware wa.me link.
 * Pass a `WhatsAppKey` to override with a named message, or a raw string
 * to use a custom message verbatim.
 */
export function useWhatsAppLink(override?: WhatsAppKey | string): string {
  const { pathname } = useLocation();
  if (override) {
    if (override in WHATSAPP_KEYS) {
      return buildWhatsAppLink(WHATSAPP_KEYS[override as WhatsAppKey]);
    }
    return buildWhatsAppLink(override);
  }
  return getWhatsAppLink(pathname);
}
